/**
 * Spaced-repetition study: one kanji at a time, revealed then graded.
 *
 * The queue is fetched once per level and worked through in order (due cards
 * first, then new ones). A card graded "again" still advances — the backend
 * shortens its interval, so it comes back in a later session rather than
 * looping here, which keeps the session length predictable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ApiError,
  LEVELS,
  api,
  levelColor,
  type Grade,
  type Level,
  type ReviewQueue,
} from '../api'
import {
  EmptyState,
  ErrorNote,
  LevelBadge,
  LevelPicker,
  Progressbar,
  Spinner,
  StatusDot,
} from '../components/ui'
import { useAuth } from '../store/auth'

const GRADES: { grade: Grade; label: string; hint: string; tint: string }[] = [
  { grade: 'again', label: 'Again', hint: '1', tint: '#f43f5e' },
  { grade: 'hard', label: 'Hard', hint: '2', tint: '#f59e0b' },
  { grade: 'good', label: 'Good', hint: '3', tint: '#3b82f6' },
  { grade: 'easy', label: 'Easy', hint: '4', tint: '#22c55e' },
]

function asLevel(value: string | null): Level | null {
  return value !== null && (LEVELS as string[]).includes(value) ? (value as Level) : null
}

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Could not reach the server.'
}

export default function Learn() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const level = asLevel(params.get('level')) ?? user?.target_level ?? 'N5'

  const [queue, setQueue] = useState<ReviewQueue | null>(null)
  const [index, setIndex] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [grading, setGrading] = useState(false)
  const [reviewed, setReviewed] = useState(0)
  const [againCount, setAgainCount] = useState(0)
  const [added, setAdded] = useState<string[]>([])
  const [adding, setAdding] = useState(false)
  const [reloads, setReloads] = useState(0)

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Which level the visible queue belongs to. Grading is async, so a result that
  // arrives after the learner has switched level must not advance the new queue.
  const levelRef = useRef(level)

  useEffect(() => {
    let alive = true
    levelRef.current = level
    void (async () => {
      setLoading(true)
      setError(null)
      // Clear the old queue, otherwise the previous level's card stays on screen
      // and stays gradeable while the new one loads.
      setQueue(null)
      setIndex(0)
      setRevealed(false)
      try {
        const data = await api.queue(level, 20)
        if (!alive) return
        setQueue(data)
        setIndex(0)
        setRevealed(false)
      } catch (err) {
        if (alive) setError(errText(err))
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [level, reloads])

  // Session tallies belong to the level, not to each reload, so "load more cards"
  // continues the same session instead of silently restarting the counters.
  useEffect(() => {
    setReviewed(0)
    setAgainCount(0)
    setAdded([])
  }, [level])

  const cards = useMemo(() => (queue ? [...queue.due, ...queue.new] : []), [queue])
  const card = index < cards.length ? cards[index] : null

  const submitGrade = useCallback(
    async (grade: Grade) => {
      const current = cards[index]
      if (!current || grading) return
      const forLevel = levelRef.current
      setGrading(true)
      setError(null)
      try {
        await api.grade(current.char, grade)
        if (!mounted.current) return
        // Level changed while the grade was in flight: the answer was still saved
        // server-side, but advancing now would skip the new queue's first card.
        if (levelRef.current !== forLevel) return
        setReviewed((n) => n + 1)
        if (grade === 'again') setAgainCount((n) => n + 1)
        setRevealed(false)
        setIndex((i) => i + 1)
      } catch (err) {
        if (mounted.current) setError(errText(err))
      } finally {
        if (mounted.current) setGrading(false)
      }
    },
    [cards, index, grading],
  )

  const addFlashcard = useCallback(async (char: string) => {
    setAdding(true)
    setError(null)
    try {
      await api.addFlashcard(char)
      if (mounted.current) setAdded((prev) => (prev.includes(char) ? prev : [...prev, char]))
    } catch (err) {
      if (mounted.current) setError(errText(err))
    } finally {
      if (mounted.current) setAdding(false)
    }
  }, [])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const tag = event.target instanceof HTMLElement ? event.target.tagName : ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      // A focused button or link already handles Space/Enter natively; hijacking
      // it there would break "＋ Flashcard" for keyboard users.
      if ((tag === 'BUTTON' || tag === 'A') && (event.code === 'Space' || event.key === 'Enter')) return

      if (event.code === 'Space') {
        event.preventDefault()
        setRevealed(true)
        return
      }
      const slot = ['1', '2', '3', '4'].indexOf(event.key)
      if (slot >= 0 && revealed) {
        event.preventDefault()
        void submitGrade(GRADES[slot].grade)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [revealed, submitGrade])

  function changeLevel(next: Level) {
    setParams(
      (prev) => {
        const search = new URLSearchParams(prev)
        search.set('level', next)
        return search
      },
      { replace: true },
    )
  }

  const inFlashcards = card ? card.in_flashcards || added.includes(card.char) : false

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-5 py-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Study</h1>
          <p className="text-sm text-ink-500">
            Grade honestly — the schedule is only as useful as the grades you give it.
          </p>
        </div>
        <LevelPicker value={level} onChange={changeLevel} />
      </header>

      {queue && (
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
          <span className="rounded-full bg-n4/15 px-2.5 py-1 text-n4">{queue.due_count} due</span>
          <span className="rounded-full bg-brand-500/15 px-2.5 py-1 text-brand-500">{queue.new_count} new</span>
          <span className="rounded-full bg-ink-800 px-2.5 py-1 text-ink-500">{reviewed} reviewed</span>
          {againCount > 0 && (
            <span className="rounded-full bg-rose-500/15 px-2.5 py-1 text-rose-300">{againCount} again</span>
          )}
        </div>
      )}

      {cards.length > 0 && (
        <div className="space-y-1.5">
          <Progressbar value={Math.min(index, cards.length)} max={cards.length} color={levelColor[level]} />
          <div className="text-[11px] text-ink-500">
            Card {Math.min(index + 1, cards.length)} of {cards.length} in this session
          </div>
        </div>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && <Spinner label="Building your queue…" />}

      {!loading && cards.length === 0 && !error && (
        <EmptyState
          title="Nothing waiting at this level"
          body="No cards are due and there is nothing new left to introduce here. Try another level or test what you know."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Link className="btn-primary" to={`/quiz?level=${level}`}>
                Take a quiz
              </Link>
              <button type="button" className="btn-ghost" onClick={() => setReloads((n) => n + 1)}>
                Check again
              </button>
            </div>
          }
        />
      )}

      {!loading && cards.length > 0 && !card && (
        <EmptyState
          title="Session complete"
          body={`${reviewed} card${reviewed === 1 ? '' : 's'} reviewed${
            againCount > 0 ? `, ${againCount} marked again for another pass` : ''
          }. Quizzing now is the cheapest way to find the gaps.`}
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Link className="btn-primary" to={`/quiz?level=${level}`}>
                Take a quiz
              </Link>
              <button type="button" className="btn-ghost" onClick={() => setReloads((n) => n + 1)}>
                Load more cards
              </button>
            </div>
          }
        />
      )}

      {card && (
        <section className="card animate-fade-up p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <LevelBadge level={card.level} />
              <StatusDot status={card.status} />
              {card.strokes !== null && <span className="text-[11px] text-ink-500">{card.strokes} strokes</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-ghost px-2.5 py-1 text-[11px]"
                disabled={adding || inFlashcards}
                onClick={() => void addFlashcard(card.char)}
              >
                <span className="jp">{inFlashcards ? '✓ Flashcard' : '＋ Flashcard'}</span>
              </button>
              <Link
                className="btn-ghost px-2.5 py-1 text-[11px]"
                // MapPage needs `level` (it otherwise falls back to the profile's
                // target level) and `sel` to actually open the detail panel;
                // `focus` alone only pans the viewport.
                to={`/map?level=${level}&sel=${encodeURIComponent(card.char)}&focus=${encodeURIComponent(card.char)}`}
              >
                Open on map
              </Link>
            </div>
          </div>

          <div className="flex flex-col items-center gap-6 py-8">
            <div className="jp select-none text-[140px] font-semibold leading-none text-slate-100">{card.char}</div>

            {revealed ? (
              <div className="space-y-1 text-center">
                <div className="text-2xl font-bold text-slate-100">{card.meaning || '—'}</div>
                <div className="text-sm uppercase tracking-[0.15em] text-brand-500">{card.romaji || '—'}</div>
                <div className="text-xs text-ink-500">
                  {card.child_count} part{card.child_count === 1 ? '' : 's'} · appears in {card.parent_count} kanji
                </div>
              </div>
            ) : (
              <button type="button" className="btn-primary px-8" onClick={() => setRevealed(true)}>
                Show answer
              </button>
            )}
          </div>

          {revealed && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {GRADES.map((g) => (
                <button
                  key={g.grade}
                  type="button"
                  disabled={grading}
                  onClick={() => void submitGrade(g.grade)}
                  className="btn flex-col gap-0.5 border py-2.5"
                  style={{ borderColor: `${g.tint}55`, background: `${g.tint}14`, color: g.tint }}
                >
                  <span>{g.label}</span>
                  <span className="text-[10px] font-normal opacity-70">{g.hint}</span>
                </button>
              ))}
            </div>
          )}

          <p className="mt-5 text-center text-[11px] text-ink-500">
            Space reveals the answer · 1 – 4 grade it
          </p>
          {(grading || adding) && (
            <div className="mt-3 flex justify-center">
              <Spinner label={grading ? 'Saving your grade…' : 'Adding to flashcards…'} />
            </div>
          )}
        </section>
      )}
    </div>
  )
}
